import React, { useState } from "react";
import {
  FileDown,
  RefreshCw,
  Bell,
  Download,
  Eye,
  PlayCircle,
  Filter,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react";
import "./distribution-styles.css";
import MaritimeLayout from "../common/Applayout";

export default function AutomatedDistributionDashboard() {
  const [settlementCycle, setSettlementCycle] = useState("Nov 2025");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedPayee, setSelectedPayee] = useState(null);

  // Summary data
  const summaryData = {
    totalPayees: 9,
    totalAmount: 734500000,
    paidPercentage: 80,
    pendingPercentage: 20,
  };

  // Settlement instructions data
  const settlementInstructions = [
    {
      id: 1,
      payee: "CEBS",
      type: "Operator",
      portEmirate: "All",
      amount: 184000000,
      iban: "AE45-CEBS-001",
      payByDate: "05-Dec-2025",
      status: "PAID",
      proof: true,
    },
    {
      id: 2,
      payee: "MOEI",
      type: "Regulator",
      portEmirate: "All",
      amount: 184000000,
      iban: "AE89-MOEI-001",
      payByDate: "05-Dec-2025",
      status: "PAID",
      proof: true,
    },
    {
      id: 3,
      payee: "Jebel Ali",
      type: "Port",
      portEmirate: "Dubai",
      amount: 241875000,
      iban: "AE23-DPM-001",
      payByDate: "05-Dec-2025",
      status: "PAID",
      proof: true,
    },
    {
      id: 4,
      payee: "Khalifa Port",
      type: "Port",
      portEmirate: "Abu Dhabi",
      amount: 76687500,
      iban: "AE12-ADP-001",
      payByDate: "05-Dec-2025",
      status: "PROCESSING",
      proof: false,
    },
    {
      id: 5,
      payee: "Sharjah Ports",
      type: "Port",
      portEmirate: "Sharjah",
      amount: 27400000,
      iban: "AE45-GULF-001",
      payByDate: "05-Dec-2025",
      status: "PENDING",
      proof: false,
    },
    {
      id: 6,
      payee: "Fujairah",
      type: "Port",
      portEmirate: "Fujairah",
      amount: 10000000,
      iban: "AE99-FUJ-001",
      payByDate: "05-Dec-2025",
      status: "PAID",
      proof: true,
    },
    {
      id: 7,
      payee: "Ras Al Khaimah",
      type: "Port",
      portEmirate: "RAK",
      amount: 5000000,
      iban: "AE77-RAK-001",
      payByDate: "05-Dec-2025",
      status: "PENDING",
      proof: false,
    },
    {
      id: 8,
      payee: "Ajman",
      type: "Port",
      portEmirate: "Ajman",
      amount: 2500000,
      iban: "AE88-AJM-001",
      payByDate: "05-Dec-2025",
      status: "PAID",
      proof: true,
    },
    {
      id: 9,
      payee: "UAQ",
      type: "Port",
      portEmirate: "UAQ",
      amount: 2500000,
      iban: "AE55-UAQ-001",
      payByDate: "05-Dec-2025",
      status: "PAID",
      proof: true,
    },
  ];

  // Distribution chart data
  const distributionData = [
    { beneficiary: "CEBS", percentage: 25, color: "#7c3aed" },
    { beneficiary: "MOEI", percentage: 25, color: "#f59e0b" },
    { beneficiary: "Ports Combined", percentage: 50, color: "#10b981" },
  ];

  // Audit trail data
  const auditTrail = [
    {
      date: "2025-12-03",
      action: "Payment batch created",
      performedBy: "system@nmc",
      description: "9 settlement instructions generated",
    },
    {
      date: "2025-12-04",
      action: "Funds transferred",
      performedBy: "treasury@moei",
      description: "CEBS & MOEI payments confirmed",
    },
    {
      date: "2025-12-05",
      action: "Port receipts validated",
      performedBy: "finance@cebs",
      description: "5 of 7 port payments confirmed",
    },
  ];

  // Filter settlements based on status
  const filteredSettlements =
    statusFilter === "All"
      ? settlementInstructions
      : settlementInstructions.filter((item) => item.status === statusFilter);

  // Helper functions
  const formatAmount = (amount) => {
    if (amount >= 1000000) {
      return `AED ${(amount / 1000000).toFixed(1)}M`;
    }
    return `AED ${amount.toLocaleString()}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "PAID":
        return <CheckCircle size={14} className="mr-1" />;
      case "PROCESSING":
        return <Clock size={14} className="mr-1" />;
      case "PENDING":
        return <AlertCircle size={14} className="mr-1" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "PAID":
        return "status-paid";
      case "PROCESSING":
        return "status-processing";
      case "PENDING":
        return "status-pending";
      default:
        return "";
    }
  };

  // Action handlers
  const handleGenerateCycle = () => {
    alert("New settlement cycle generation initiated");
  };

  const handleExportReport = () => {
    alert("Settlement report exported");
  };

  const handleViewReceipts = () => {
    alert("Opening receipts view");
  };

  const handleNotifyPayees = () => {
    alert("Payee notifications sent");
  };

  const handleRefresh = () => {
    alert("Data refreshed");
  };

  const handleViewProof = (payee) => {
    setSelectedPayee(payee);
    alert(`Viewing proof for ${payee}`);
  };

  return (
    <MaritimeLayout>
      {" "}
      <div className="distribution-dashboard">
        {/* Header */}
        <div className="distribution-header">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="distribution-title">
                National Maritime Center - Automated Distribution Dashboard
              </h1>
              <p className="text-slate-600 text-lg">
                Automated settlement distribution and payment tracking system
              </p>
            </div>
          </div>

          {/* Control Bar */}
          <div className="control-bar">
            <div className="flex items-center gap-4">
              <div>
                <label className="text-sm text-slate-600 mr-2">
                  Settlement Cycle:
                </label>
                <select
                  className="control-select"
                  value={settlementCycle}
                  onChange={(e) => setSettlementCycle(e.target.value)}
                >
                  <option value="Nov 2025">Nov 2025</option>
                  <option value="Oct 2025">Oct 2025</option>
                  <option value="Sep 2025">Sep 2025</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-600 mr-2">Status:</label>
                <select
                  className="control-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="All">All</option>
                  <option value="PAID">Paid</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
              <div className="control-badge">Active Cycle</div>
            </div>
            <button
              className="primary-button btn-secondary ml-auto"
              onClick={handleRefresh}
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-value">
              {summaryData.totalPayees} Entities
            </div>
            <div className="summary-label">Total Payees</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">
              {formatAmount(summaryData.totalAmount)}
            </div>
            <div className="summary-label">Total Amount</div>
          </div>
          <div className="summary-card">
            <div className="summary-value text-green-600">
              {summaryData.paidPercentage}%
            </div>
            <div className="summary-label">Paid %</div>
          </div>
          <div className="summary-card">
            <div className="summary-value text-amber-600">
              {summaryData.pendingPercentage}%
            </div>
            <div className="summary-label">Pending %</div>
          </div>
        </div>

        {/* Settlement Instruction Table */}
        <div className="table-container">
          <div className="p-6 border-b border-slate-100">
            <h3 className="text-lg font-semibold text-slate-800">
              Settlement Instruction Table
            </h3>
          </div>
          <table className="distribution-table">
            <thead>
              <tr>
                <th>Payee</th>
                <th>Type</th>
                <th>Port / Emirate</th>
                <th>Amount (AED)</th>
                <th>IBAN</th>
                <th>Pay-By Date</th>
                <th>Status</th>
                <th>Proof</th>
              </tr>
            </thead>
            <tbody>
              {filteredSettlements.map((item) => (
                <tr key={item.id}>
                  <td className="font-semibold text-slate-800">{item.payee}</td>
                  <td className="text-slate-600">{item.type}</td>
                  <td className="text-slate-600">{item.portEmirate}</td>
                  <td className="font-semibold text-slate-800">
                    {formatAmount(item.amount)}
                  </td>
                  <td className="font-mono text-sm text-slate-600">
                    {item.iban}
                  </td>
                  <td className="text-slate-600">{item.payByDate}</td>
                  <td>
                    <span
                      className={`status-badge ${getStatusBadge(item.status)}`}
                    >
                      {getStatusIcon(item.status)}
                      {item.status}
                    </span>
                  </td>
                  <td>
                    {item.proof ? (
                      <button
                        className="action-button"
                        onClick={() => handleViewProof(item.payee)}
                      >
                        <Eye size={14} className="mr-1" />
                        Receipt
                      </button>
                    ) : (
                      <span className="text-slate-400">--</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Chart and Audit Section */}
        <div className="chart-section">
          {/* Distribution Chart */}
          <div className="chart-container">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Distribution Chart - Amount by Beneficiary
            </h3>
            <div className="distribution-chart">
              {distributionData.map((item, index) => (
                <div
                  key={index}
                  className={`chart-item ${item.beneficiary
                    .toLowerCase()
                    .replace(" ", "-")}`}
                >
                  <span className="font-semibold text-slate-800">
                    {item.beneficiary}
                  </span>
                  <div className="chart-bar">
                    <div
                      className={`chart-fill ${item.beneficiary
                        .toLowerCase()
                        .replace(" ", "-")}`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                  <span className="font-bold text-slate-800">
                    {item.percentage}%
                  </span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-600 mt-4">
              Revenue distribution follows the PPP model: 25% CEBS (Operations),
              25% MOEI (Regulation), 50% Ports (Operations)
            </p>
          </div>

          {/* Audit Trail */}
          <div className="chart-container">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Audit Trail
            </h3>
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Action</th>
                  <th>Performed By</th>
                </tr>
              </thead>
              <tbody>
                {auditTrail.map((item, index) => (
                  <tr key={index}>
                    <td className="font-mono text-sm">{item.date}</td>
                    <td className="font-semibold">{item.action}</td>
                    <td className="text-slate-600">{item.performedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-sm text-slate-600 mt-3">
              Full audit trail maintained for compliance and transparency
            </p>
          </div>
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          <button
            className="primary-button btn-primary"
            onClick={handleGenerateCycle}
          >
            <PlayCircle size={16} className="mr-2" />
            Generate New Cycle
          </button>
          <button
            className="primary-button btn-success"
            onClick={handleExportReport}
          >
            <Download size={16} className="mr-2" />
            Export Settlement Report
          </button>
          <button
            className="primary-button btn-secondary"
            onClick={handleViewReceipts}
          >
            <Eye size={16} className="mr-2" />
            View Receipts
          </button>
          <button
            className="primary-button btn-secondary"
            onClick={handleNotifyPayees}
          >
            <Bell size={16} className="mr-2" />
            Notify Payees
          </button>
        </div>
      </div>
    </MaritimeLayout>
  );
}
